import React, { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { HugeiconsIcon } from '@hugeicons/react';

/**
 * Design system icon renderer. Every icon in the dashboard is a Hugeicon
 * (free set, stroke rounded) drawn in currentColor at strokeWidth 2.
 */
export const ICON_STROKE_WIDTH = 2;

// Solid rendition of a Hugeicon (for "active" states such as a liked thumb or a set star).
const SolidGlyph = ({ icon, strokeWidth }) => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    {icon.map(([tag, attrs]) =>
      React.createElement(tag, {
        ...attrs,
        fill: 'currentColor',
        strokeWidth,
      })
    )}
  </svg>
);

/**
 * Generic Hugeicon sized by font-size (1em). Use it for solid icons; plain icons use
 * `<HugeiconsIcon icon={...} strokeWidth={2} size="1em" />` from '@hugeicons/react' directly.
 */
export const HugeIcon = forwardRef(
  (
    {
      icon,
      solid = false,
      spin = false,
      rotate,
      className,
      style,
      strokeWidth = ICON_STROKE_WIDTH,
      ...rest
    },
    ref
  ) => (
    <span
      ref={ref}
      role="img"
      className={cn(
        'inline-flex items-center justify-center align-[-0.125em] leading-[0] [&>svg]:inline-block [&>svg]:shrink-0',
        spin && 'animate-spin',
        className
      )}
      style={rotate ? { ...style, transform: `rotate(${rotate}deg)` } : style}
      {...rest}
    >
      {solid ? (
        <SolidGlyph icon={icon} strokeWidth={strokeWidth} />
      ) : (
        <HugeiconsIcon
          icon={icon}
          size="1em"
          strokeWidth={strokeWidth}
          color="currentColor"
          aria-hidden="true"
          focusable="false"
        />
      )}
    </span>
  )
);

HugeIcon.displayName = 'HugeIcon';

export default HugeIcon;
